'use client';

import { useCallback, useEffect, useRef } from 'react';

// How close to the bottom (px) the reader must be for a growing container to keep pulling the
// viewport down. Past this, they've scrolled up to read and we leave them there.
const DEFAULT_BOTTOM_THRESHOLD_PX = 80;

interface UseScrollFollowOptions {
  /**
   * True while content is actively being produced into the container; its rising edge re-engages
   * follow and snaps to the bottom. Not an enable switch — the hook follows regardless of this.
   */
  isStreaming: boolean;
  /**
   * How close to the bottom (px) counts as "at the bottom" when deciding whether to re-engage
   * follow after the reader scrolls. Defaults to 80.
   */
  bottomThresholdPx?: number;
}

/**
 * ### useScrollFollow
 *
 * @description
 * Keeps a scroll container pinned to its bottom as its content grows, while yielding to a reader
 * who scrolls up to read. Follow re-engages when the reader scrolls back to the bottom, or on the
 * rising edge of `isStreaming` (a fresh burst snaps back even if they had scrolled away during the
 * previous one).
 *
 * Follow is driven by a `ResizeObserver` across the container's element children rather than by a
 * data array, so growth that is not a data change — a chart resolving its height after first paint,
 * a composer autosizing as the reader types — still keeps the view pinned. The container must be the
 * scroll parent; this hook does not style it.
 *
 * **Contract:** the container's direct children are observed once, at mount. Content must grow
 * *within* those children rather than by adding or removing them — a container that gains children
 * at runtime will silently stop following whatever appears in the new ones.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useScrollFollow(ref, { isStreaming: isLoading });
 * return <div ref={ref}>…</div>;
 *
 * @param scrollRef - Ref of the scroll container to keep pinned to the bottom.
 * @param options - `isStreaming`: re-engage follow on its rising edge. `bottomThresholdPx`: optional
 * re-engage distance, default 80.
 * @returns Nothing; the hook drives the container's scroll as a side effect.
 */
const useScrollFollow = (
  scrollRef: React.RefObject<HTMLElement | null>,
  { isStreaming, bottomThresholdPx = DEFAULT_BOTTOM_THRESHOLD_PX }: UseScrollFollowOptions,
) => {
  // Whether a growing container should keep pulling the viewport down. Off once the reader scrolls
  // up; re-engaged when they return to the bottom or a fresh burst of content starts.
  const shouldFollowRef = useRef(true);
  const wasStreamingRef = useRef(false);
  // Holds the scrollTop the hook itself last scrolled to, so the scroll handler can tell its own
  // programmatic snap from a genuine reader gesture instead of latching follow off.
  const lastWrittenScrollTopRef = useRef<number | null>(null);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container || typeof container.scrollTo !== 'function') {
      return;
    }

    container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    // Read the position back rather than storing scrollHeight: the browser clamps to the
    // maximum offset, and the clamped value is what the resulting scroll event will carry.
    lastWrittenScrollTopRef.current = container.scrollTop;
  }, [scrollRef]);

  // The programmatic snap-to-bottom dispatches a scroll event asynchronously; by the time it fires,
  // content has grown scrollHeight, so the handler would measure that grown height against a stale
  // scrollTop, read past the threshold, and latch follow off for the rest of the turn. Recognize
  // and swallow the event carrying our own scroll.
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    // Consume the position our own scroll wrote — once, on the very next event, whatever it is.
    // Scroll events are dispatched in order, so the event our write produced is the next one to
    // arrive; anything later is the reader. Clearing unconditionally matters: leaving a stale value
    // behind would swallow a genuine gesture that happens to land on that same offset, which is how
    // a reader scrolling back to the bottom could fail to re-engage follow.
    const ownScrollTop = lastWrittenScrollTopRef.current;
    lastWrittenScrollTopRef.current = null;
    if (ownScrollTop === container.scrollTop) {
      return;
    }

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldFollowRef.current = distanceFromBottom <= bottomThresholdPx;
  }, [scrollRef, bottomThresholdPx]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollRef, handleScroll]);

  // A fresh burst (in chat: the reader just submitted) snaps back to the bottom even if they had
  // scrolled up during the previous one.
  //
  // Scroll here rather than only arming the flag. The burst's first growth — in chat, the submitted
  // message and the thinking indicator — lands in the same commit that flips `isStreaming`, and the
  // observer below is delivered before this passive effect runs. Arming alone would leave that
  // first growth evaluated against the previous burst's flag, so a reader who had scrolled away
  // would not be snapped back at all.
  useEffect(() => {
    if (isStreaming && !wasStreamingRef.current) {
      shouldFollowRef.current = true;
      scrollToBottom();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, scrollToBottom]);

  // Drive follow from content size, not a data array. Observe every element child rather than a
  // single content element: the sticky composer lives inside the scroll container and autosizes
  // unbounded, so it grows scrollHeight without changing the transcript's own box.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (shouldFollowRef.current) {
        scrollToBottom();
      }
    });

    Array.from(container.children).forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [scrollRef, scrollToBottom]);
};

export default useScrollFollow;
